import { ref, computed, watch, onMounted, nextTick } from 'vue';
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import StickyScroller from '../../scripts/lib/sticky-scroller';
import { calculateVisiblePx } from './image-visibility';
import imageViewThumbnails from '../image-view-thumbnails/image-view-thumbnails.vue.component';

export default {
  template: document.getElementById('image-main-image-view-template').innerHTML,

  components: {
    'image-view-thumbnails': imageViewThumbnails,
  },

  props: {
    productId: {
      type: Number,
      required: true,
    },
    images: {
      type: Array,
      required: true,
    },
    previewImage: {
      type: Object,
      required: false,
      default: null,
    },
    active: {
      type: Object,
      required: true,
    },
    brandName: {
      type: String,
      required: true,
    },
    productName: {
      type: String,
      required: true,
    },
    modelMeasurementsInfo: {
      type: Boolean,
      required: false,
    },
  },
  setup(props) {
    const swiper = ref(null);
    const swiperInstance = ref(null);
    const swiperIndex = ref(0);
    const activeThumbnail = ref(null);
    const imageTrackingSent = ref({});
    const observer = ref(null);
    const pageLoaded = ref({});
    const pageResized = ref(false);

    const showPreviewImage = computed(
      () =>
        props.previewImage &&
        props.images[0]?.fallbackStandardRedesignUrl !== props.previewImage.fallbackStandardRedesignUrl,
    );

    const imageAltTag = computed(() => {
      return `${props.brandName} ${props.productName}${props.active.colors?.active?.name ? ': ' + props.active.colors.active.name : ''}`.trim();
    });

    function buildSrcSet(view) {
      return (
        [
          [view.thumbnailUrl, '144w'],
          [view.thumbnailRetinaUrl, '288w'],
          [view.standardUrl, '435w'],
          [view.standardRedesignUrl, '680w'],
          [view.standardRetinaUrl, '870w'],
          [view.zoomUrl, '1122w'],
          [view.standardRedesignRetinaUrl, '1360w'],
          [view.zoomRetinaUrl, '2244w'],
        ]
          // remove non existent urls
          .filter(([src]) => !!src)
          // build srcset string
          .map(([src, size]) => `${src} ${size}`)
          .join(',\n')
      );
    }

    function openZoomOverlay(index) {
      document.dispatchEvent(new CustomEvent('ents:zoom-layer:open', { detail: { index } }));
    }

    function setPageLoaded() {
      pageLoaded.value = true;
    }

    function initializeSwiperSlider() {
      if (swiperInstance.value) {
        swiperInstance.value.destroy();
        swiperInstance.value = null;
      }

      swiperInstance.value = new Swiper(swiper.value, {
        modules: [Navigation, Pagination],
        enabled: props.images.length > 1,
        initialSlide: swiperIndex.value,
        autoplay: false,
        loop: true,
        spaceBetween: 10,
        pagination: {
          el: '.ents-image-view__mobile--pagination',
          clickable: true,
        },
        navigation: {
          prevEl: '.swiper-button-prev',
          nextEl: '.swiper-button-next',
        },
        breakpoints: {
          560: {
            slidesPerView: 1.6,
            centeredSlides: true,
            // This can be changed back to true once swiper #7239 has been resolved
            loop: props.images.length > 3,
          },
          // 710 and 860 are not breakpoints we usually use in our library.
          // When the slider only gets wider but not higher anymore, it would make the images float away from each other.
          // So we need the breakpoints in this case to show "more" of the cut-off images and preserve the spacings between them.
          710: {
            slidesPerView: 2,
            centeredSlides: true,
            // This can be changed back to true once swiper #7239 has been resolved
            loop: props.images.length > 3,
          },
          860: {
            slidesPerView: 2.2,
            centeredSlides: true,
            // This can be changed back to true once swiper #7239 has been resolved
            loop: props.images.length > 3,
          },
          960: {
            loop: false,
          },
        },
        on: {
          slideChange: () => {
            if (swiperInstance.value && !swiperInstance.value.destroyed) {
              if (Number.isNaN(swiperInstance.value.realIndex)) {
                swiperIndex.value = 0;
              } else {
                swiperIndex.value = swiperInstance.value.realIndex;
              }
            }
          },
          beforeResize: () => {
            // reinit swiper on page resize so all slides are properly shown
            // solves bug with disappearing slide to the left of the active slide
            //
            // 'pageResized' flag is necessary because Swiper's resize handler
            // is always triggered once after initialization, but we only want
            // to reinit Swiper on actual page resizes
            if (pageResized.value) {
              initializeSwiperSlider();
              pageResized.value = false;
            } else {
              pageResized.value = true;
            }
          },
        },
      });
    }

    function imageObserver() {
      if (observer.value instanceof IntersectionObserver) {
        observer.value.disconnect();
        imageTrackingSent.value = {};
      }

      const options = {
        root: null,
        rootMargin: '0px',
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
      };

      const desktopImages = document.querySelectorAll('.ents-image-view__desktop .ents-image-view__entry');
      const mobileImages = document.querySelectorAll('.ents-image-view__mobile .swiper-slide');

      observer.value = new IntersectionObserver(() => {
        compareDesktopImageVisibility(desktopImages);
      }, options);

      Array.from(desktopImages)
        .concat(Array.from(mobileImages))
        .forEach((el) => {
          const imageIndex =
            el.getAttribute('data-ents-image-view-index') || el.getAttribute('data-swiper-slide-index');
          imageTrackingSent.value[imageIndex] = false;
          observer.value.observe(el);
        });
    }

    function compareDesktopImageVisibility(els) {
      let max = 0;

      for (const el of Array.from(els)) {
        if (el.offsetHeight === 0) {
          // important, otherwise visiblePercentage will be "infinity", which is > 40%
          return;
        }
        const elementIndex = el.getAttribute('data-ents-image-view-index');

        // calculate number of visible pixels (vertically, i.e. rows)
        const visiblePx = calculateVisiblePx(true, el);

        if (visiblePx > max) {
          max = visiblePx;
          activeThumbnail.value = parseInt(elementIndex, 10);
        }
      }
    }

    onMounted(() => {
      initializeSwiperSlider();
      imageObserver();

      new StickyScroller(document.querySelector('.ents-pds-summary__wrapper--sticky'));

      window.addEventListener('load', () => {
        setPageLoaded();
      });
    });

    watch(
      () => props.images,
      () => {
        nextTick(() => {
          initializeSwiperSlider();
          imageObserver();
        });
      },
    );

    return {
      showPreviewImage,
      swiperIndex,
      activeThumbnail,
      buildSrcSet,
      openZoomOverlay,
      swiper,
      imageAltTag,
    };
  },
};
